import ChatRoundedIcon from '@material-ui/icons/ChatRounded';
import HomeRoundedIcon from '@material-ui/icons/HomeRounded';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import MenuRoundedIcon from '@material-ui/icons/MenuRounded';
import SpeakerNotesOffRoundedIcon from '@material-ui/icons/SpeakerNotesOffRounded';
import { Link } from '@reach/router';
import React, { useContext } from 'react';
import styled, { ThemeContext } from 'styled-components/macro';
import { ChatsContext } from '../../../contexts/Chats';
import { SaveChangesContext } from '../../../contexts/SaveChanges';
import { useWindowSize } from '../../../hooks/useWindowSize';
import {
  focusRing,
  fontSize,
  fontWeight,
  layout,
  media,
  neutral,
  radius,
  shadow,
  space,
  transition,
  zIndex,
} from '../../../theme/tokens';
import ProfileImage from '../ProfileImage';

export const NavContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${space[3]};
  height: 100%;
  padding: 0 ${layout.gutter};

  overflow: auto;
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  ${media.md`
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    gap: ${space[4]};
    padding: ${space[5]} ${space[3]};
  `}

  /* staff sidebar: text links, all the same width so active highlights line up.
     The student sidebar is a centred column of icons with their badges. */
  ${({ $fullWidthItems }) =>
    $fullWidthItems &&
    media.md`
      align-items: stretch;
      gap: ${space[2]};

      & > .MuiBadge-root,
      & > button,
      & > a {
        width: 100%;
      }
      & > a {
        margin-bottom: ${space[3]};
      }
    `}
`;

export const NavBar = styled.nav`
  height: ${layout.mobileNavHeight};
  width: 100%;
  position: fixed;
  top: 0;
  background: ${neutral.surface};
  border-bottom: 1px solid ${neutral.border};
  box-shadow: ${shadow.sm};
  z-index: ${zIndex.nav};

  ${media.md`
    height: 100%;
    width: ${layout.sidebarWidth};
    left: 0;
    border-bottom: none;
    border-right: 1px solid ${neutral.border};
    box-shadow: none;
  `}
`;

export const NavDrawer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${space[2]};
  height: 100%;
  min-width: 26rem;
  padding: ${space[6]} ${space[4]};
  font-family: ${({ theme }) => theme.fontFamily};

  overflow: auto;
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  & > * {
    align-self: stretch;
  }
`;

export const UserAvatarImage = styled(ProfileImage)`
  width: 4rem;
  height: 4rem;
`;

export const TeacherAvatarImage = styled(ProfileImage)`
  width: 5.6rem;
  height: 5.6rem;
  margin: 0;
`;

export const ProfileLink = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: ${layout.touchTarget};
  min-height: ${layout.touchTarget};
  border-radius: ${radius.pill};
  ${focusRing}
`;

export const NavLink = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: ${layout.touchTarget};
  min-height: ${layout.touchTarget};
  border-radius: ${radius.md};
  color: ${({ theme }) => theme.headerColour};
  transition: background ${transition.fast};

  &:hover {
    background: ${neutral.subtle};
  }
  ${focusRing}
`;

export const ActiveLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${layout.touchTarget};
  width: 100%;
  margin: 0;
  padding: ${space[2]} ${space[3]};
  border-radius: ${radius.md};
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  color: ${({ theme }) => theme.headerColour};
  text-align: center;
  text-decoration: none;
  transition: background ${transition.fast}, color ${transition.fast};

  &:hover {
    background: ${neutral.subtle};
  }
  &[data-ispartiallycurrent='true'] {
    background: ${neutral.subtle};
    box-shadow: inset 4px 0 0 ${({ theme }) => theme.buttonPrimary};
    font-weight: ${fontWeight.heavy};
  }
  ${focusRing}

  ${media.md`
    font-size: ${fontSize.sm};
    padding: ${space[2]};
  `}
`;

export const TeacherNavLink = ({ afterClick, ...props }) => {
  const saveChangesStore = useContext(SaveChangesContext);
  const windowStore = useWindowSize();
  const chatsStore = useContext(ChatsContext);
  const getProps = ({ isPartiallyCurrent, isCurrent }) => {
    return (isPartiallyCurrent && props.to !== '/') || isCurrent
      ? { 'data-ispartiallycurrent': true }
      : {};
  };
  return (
    <ActiveLink
      onClick={(e) => {
        saveChangesStore.handleNavigate(e, windowStore.isMobile, chatsStore);
        if (afterClick) afterClick();
      }}
      getProps={getProps}
      {...props}
    />
  );
};

export const BigMailOutlineIcon = () => {
  const theme = useContext(ThemeContext);
  return <MailOutlineIcon style={{ fontSize: 26, color: theme.textDark }} />;
};

export const BigHomeIcon = () => {
  const theme = useContext(ThemeContext);
  return <HomeRoundedIcon style={{ fontSize: 26, color: theme.textDark }} />;
};

export const BigMenuIcon = () => {
  const theme = useContext(ThemeContext);
  return <MenuRoundedIcon style={{ fontSize: 26, color: theme.textDark }} />;
};

export const OpenChatIcon = () => {
  const theme = useContext(ThemeContext);
  return <ChatRoundedIcon style={{ fontSize: 26, color: theme.textDark }} />;
};

export const CloseChatIcon = () => {
  const theme = useContext(ThemeContext);
  return (
    <SpeakerNotesOffRoundedIcon
      style={{ fontSize: 26, color: theme.textDark }}
    />
  );
};

export * from './Form';
export * from './Notifications';
